/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { exists, mapValues } from '../runtime';
import type { ProposalOutcome } from './ProposalOutcome';
import {
    ProposalOutcomeFromJSON,
    ProposalOutcomeFromJSONTyped,
    ProposalOutcomeToJSON,
} from './ProposalOutcome';

/**
 * The question being asked at the shareholder meeting. Sometimes referred to as a resolution.
 * @export
 * @interface Proposal
 */
export interface Proposal {
    /**
     * The proposal identifier.
     * @type {string}
     * @memberof Proposal
     */
    id: string;
    /**
     * The identifier of the general meeting this proposal relates to.
     * @type {string}
     * @memberof Proposal
     */
    generalMeetingId: string;
    /**
     * The identifier of the organization this proposal relates to.
     * @type {string}
     * @memberof Proposal
     */
    organizationId: string;
    /**
     * Identifier given by the organization to identify this proposal amongst the proposals submitted for consideration at the general meeting.
     * @type {string}
     * @memberof Proposal
     */
    sequenceIdentifier: string;
    /**
     * The title of the proposal given by the organization.
     * @type {string}
     * @memberof Proposal
     */
    title: string;
    /**
     * Markdown [(CommonMark)](https://commonmark.org/) formatted description of the proposal given by the organization.
     * @type {string}
     * @memberof Proposal
     */
    formattedDescription?: string;
    /**
     * Whether the proposal was submitted by the management or the shareholders of the organization.
     * @type {string}
     * @memberof Proposal
     */
    source: ProposalSourceEnum;
    /**
     * The recommendation from the management of the organization recommending which way shareholders should vote. If the management did not provide a recommendation the value is `none`. The recommended option must be a valid voting option (see `votingOptions` parameter).
     * @type {string}
     * @memberof Proposal
     */
    managementRecommendation: ProposalManagementRecommendationEnum;
    /**
     * Markdown [(CommonMark)](https://commonmark.org/) formatted description explaining why the recommendation was made.
     * @type {string}
     * @memberof Proposal
     */
    managementRecommendationDescription?: string;
    /**
     * The threshold that must be exceeded for the proposal to be approved. On the scale 0 to 1.0.
     * @type {number}
     * @memberof Proposal
     */
    passThreshold?: number;
    /**
     * 
     * @type {ProposalOutcome}
     * @memberof Proposal
     */
    outcome?: ProposalOutcome;
    /**
     * The ordinal position of the proposal within the proxy document.
     * @type {number}
     * @memberof Proposal
     */
    sequenceNumber?: number;
    /**
     * True if there is to be a vote on this proposal, false otherwise.
     * @type {boolean}
     * @memberof Proposal
     */
    votable: boolean;
    /**
     * The proposal text where it exceeds 250 characters.
     * @type {string}
     * @memberof Proposal
     */
    fullText?: string;
    /**
     * Array of 2-letter classification codes that apply to this proposal, from the following list of valid options:
     *   - AW (Animal Welfare);
     *   - AU (Audit);
     *   - CS (Capital Structure);
     *   - CC (Climate Change);
     *   - CT (Corporate Transaction);
     *   - DS (Defence & Security);
     *   - EQ (Equality);
     *   - HE (Health);
     *   - HR (Human Rights);
     *   - LO (Lobbying);
     *   - MI (Miscellaneous);
     *   - BE (Other Election);
     *   - OE (Other Environment);
     *   - OS (Other Social);
     *   - OG (Other Governance);
     *   - PN (Pay - New Deals);
     *   - PA (Pay - Annual Remuneration Report);
     *   - PR (Pay - Remuneration Policy);
     *   - SC (Shareholder Proposed Election Candidate).
     * 
     * The list of classification codes may change in the future.
     * @type {Array<string>}
     * @memberof Proposal
     */
    classificationCodes?: Array<string>;
    /**
     * A list of voting options for the proposal.
     * 
     * When there is no data available for a proposal, the available voting options will be an empty list
     * @type {Array<string>}
     * @memberof Proposal
     */
    votingOptions?: Array<ProposalVotingOptionsEnum>;
}


/**
 * @export
 */
export const ProposalSourceEnum = {
    Management: 'management',
    Shareholder: 'shareholder'
} as const;
export type ProposalSourceEnum = typeof ProposalSourceEnum[keyof typeof ProposalSourceEnum];

/**
 * @export
 */
export const ProposalManagementRecommendationEnum = {
    For: 'for',
    Against: 'against',
    Abstain: 'abstain',
    Withhold: 'withhold',
    NoAction: 'no_action',
    None: 'none'
} as const;
export type ProposalManagementRecommendationEnum = typeof ProposalManagementRecommendationEnum[keyof typeof ProposalManagementRecommendationEnum];

/**
 * @export
 */
export const ProposalVotingOptionsEnum = {
    For: 'for',
    Against: 'against',
    Withhold: 'withhold',
    Abstain: 'abstain',
    NoAction: 'no_action'
} as const;
export type ProposalVotingOptionsEnum = typeof ProposalVotingOptionsEnum[keyof typeof ProposalVotingOptionsEnum];


/**
 * Check if a given object implements the Proposal interface.
 */
export function instanceOfProposal(value: object): boolean {
    let isInstance = true;
    isInstance = isInstance && "id" in value;
    isInstance = isInstance && "generalMeetingId" in value;
    isInstance = isInstance && "organizationId" in value;
    isInstance = isInstance && "sequenceIdentifier" in value;
    isInstance = isInstance && "title" in value;
    isInstance = isInstance && "source" in value;
    isInstance = isInstance && "managementRecommendation" in value;
    isInstance = isInstance && "votable" in value;

    return isInstance;
}

export function ProposalFromJSON(json: any): Proposal {
    return ProposalFromJSONTyped(json, false);
}

export function ProposalFromJSONTyped(json: any, ignoreDiscriminator: boolean): Proposal {
    if ((json === undefined) || (json === null)) {
        return json;
    }
    return {
        
        'id': json['id'],
        'generalMeetingId': json['generalMeetingId'],
        'organizationId': json['organizationId'],
        'sequenceIdentifier': json['sequenceIdentifier'],
        'title': json['title'],
        'formattedDescription': !exists(json, 'formattedDescription') ? undefined : json['formattedDescription'],
        'source': json['source'],
        'managementRecommendation': json['managementRecommendation'],
        'managementRecommendationDescription': !exists(json, 'managementRecommendationDescription') ? undefined : json['managementRecommendationDescription'],
        'passThreshold': !exists(json, 'passThreshold') ? undefined : json['passThreshold'],
        'outcome': !exists(json, 'outcome') ? undefined : ProposalOutcomeFromJSON(json['outcome']),
        'sequenceNumber': !exists(json, 'sequenceNumber') ? undefined : json['sequenceNumber'],
        'votable': json['votable'],
        'fullText': !exists(json, 'fullText') ? undefined : json['fullText'],
        'classificationCodes': !exists(json, 'classificationCodes') ? undefined : json['classificationCodes'],
        'votingOptions': !exists(json, 'votingOptions') ? undefined : json['votingOptions'],
    };
}

export function ProposalToJSON(value?: Proposal | null): any {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) {
        return null;
    }
    return {
        
        'id': value.id,
        'generalMeetingId': value.generalMeetingId,
        'organizationId': value.organizationId,
        'sequenceIdentifier': value.sequenceIdentifier,
        'title': value.title,
        'formattedDescription': value.formattedDescription,
        'source': value.source,
        'managementRecommendation': value.managementRecommendation,
        'managementRecommendationDescription': value.managementRecommendationDescription,
        'passThreshold': value.passThreshold,
        'outcome': ProposalOutcomeToJSON(value.outcome),
        'sequenceNumber': value.sequenceNumber,
        'votable': value.votable,
        'fullText': value.fullText,
        'classificationCodes': value.classificationCodes,
        'votingOptions': value.votingOptions,
    };
}

